import React from 'react';
import {
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Tooltip
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { errorMessage } from 'utils/constants';
import {
    Button,
    Grid,
    InputLabel,
    Stack,
    TextField,
    Box,
    Checkbox,
    FormControlLabel,
    FormHelperText,
    FormControl
} from '@mui/material';
import { TimePicker } from 'antd';
import Autocomplete from '../common/Autocomplete';
import { dayPickerOptions } from '../../utils/constants/day-picker';
import { DeleteFilled } from '@ant-design/icons';
import dayjs from 'dayjs';

const returnError = (errors, field) => errors[field];
const returnTouched = (touched, field) => touched[field];

const InviteVisitorModal = ({ onClose, onSubmit, guestInvite, type, onDelete }) => {
    const isEdit = type === 'edit';

    const getInitialAccessDays = () => {
        if (isEdit && !guestInvite?.access_days) {
            return [{ label: 'Any Day', value: '' }];
        }
        if (guestInvite?.access_days) {
            return guestInvite.access_days.map(
                day => dayPickerOptions.find(opt => opt.value === day) || []
            );
        }
        return [];
    };

    const getInitialTimeRange = () => {
        if (!guestInvite?.access_start_time || !guestInvite?.access_end_time) {
            return null;
        }
        return [
            dayjs()
                .hour(guestInvite.access_start_time.hours)
                .minute(guestInvite.access_start_time.minutes),
            dayjs()
                .hour(guestInvite.access_end_time.hours)
                .minute(guestInvite.access_end_time.minutes)
        ];
    };

    const initialValues = {
        first_name: guestInvite?.first_name || '',
        last_name: guestInvite?.last_name || '',
        company_name: guestInvite?.company_name || '',
        access_days: getInitialAccessDays(),
        time_range: getInitialTimeRange(),
        anytime_access: !guestInvite?.access_start_time && !guestInvite?.access_end_time
    };

    const validationSchema = Yup.object().shape(
        {
            first_name: Yup.string()
                .trim()
                .lowercase()
                .when(['last_name', 'company_name'], {
                    is: (last_name, company_name) =>
                        !company_name?.trim() || !!last_name?.trim(),
                    then: schema => schema.required(errorMessage.firstName.required),
                    otherwise: schema => schema
                }),
            last_name: Yup.string()
                .trim()
                .lowercase()
                .when(['first_name', 'company_name'], {
                    is: (first_name, company_name) =>
                        !company_name?.trim() || !!first_name?.trim(),
                    then: schema => schema.required(errorMessage.lastName.required),
                    otherwise: schema => schema
                }),
            company_name: Yup.string().when(['first_name', 'last_name'], {
                is: (first_name, last_name) => !first_name?.trim() && !last_name?.trim(),
                then: schema => schema.required(errorMessage.fullNameOrCompany.required),
                otherwise: schema => schema
            }),
            access_days: Yup.array().min(1, errorMessage.accessDays.required),
            anytime_access: Yup.boolean(),
            time_range: Yup.mixed()
                .when('anytime_access', {
                    is: false,
                    then: schema => schema.required(errorMessage.timeRange.required),
                    otherwise: schema => schema
                })
                .nullable()
        },
        [
            ['first_name', 'last_name'],
            ['first_name', 'company_name'],
            ['last_name', 'company_name']
        ]
    );

    const handleSubmit = async values => {
        let accessEndTime = null;
        let accessStartTime = null;

        if (values.time_range && !values.anytime_access) {
            accessStartTime = {
                hours: values.time_range[0].$H,
                minutes: values.time_range[0].$m
            };
            accessEndTime = {
                hours: values.time_range[1].$H,
                minutes: values.time_range[1].$m
            };
        }

        let accessDays = null;
        if (!values.access_days.filter(day => day.label === 'Any Day').length) {
            accessDays = values.access_days.map(day => day.value);
        }

        const payload = {
            first_name: values.first_name.trim().toLowerCase(),
            last_name: values.last_name.trim().toLowerCase(),
            company_name: values.company_name.trim().toLowerCase(),
            access_start_time: accessStartTime,
            access_end_time: accessEndTime,
            access_days: accessDays
        };
        onSubmit(payload);
        onClose();
    };

    return (
        <>
            <DialogTitle>
                {type === 'edit' ? 'Edit Visitor' : 'Add New Visitor'}
            </DialogTitle>
            <Divider />
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
                validateOnChange
                enableReinitialize
            >
                {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    setFieldValue,
                    touched,
                    values
                }) => (
                    <>
                        <DialogContent sx={{ p: 2.5, maxHeight: '70vh' }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>First Name</InputLabel>
                                        <TextField
                                            required
                                            id="firstName"
                                            name="first_name"
                                            placeholder="First Name"
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.first_name && !!errors.first_name
                                            }
                                            helperText={
                                                touched.first_name && errors.first_name
                                            }
                                            value={values.first_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{
                                                sx: { mx: 0 }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Last Name</InputLabel>
                                        <TextField
                                            required
                                            id="lastName"
                                            name="last_name"
                                            placeholder="Last Name"
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={
                                                touched.last_name && !!errors.last_name
                                            }
                                            helperText={
                                                touched.last_name && errors.last_name
                                            }
                                            value={values.last_name}
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{
                                                sx: { mx: 0 }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Company Name</InputLabel>
                                        <TextField
                                            required
                                            id="companyName"
                                            name="company_name"
                                            placeholder="Company Name"
                                            fullWidth
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.company_name}
                                            error={
                                                touched.company_name &&
                                                !!errors.company_name
                                            }
                                            helperText={
                                                touched.company_name &&
                                                errors.company_name
                                            }
                                            inputProps={{
                                                style: { textTransform: 'capitalize' }
                                            }}
                                            FormHelperTextProps={{
                                                sx: { mx: 0 }
                                            }}
                                        />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Access Days</InputLabel>
                                        <FormControl error variant="standard">
                                            <Autocomplete
                                                multiple
                                                id="access-days"
                                                options={dayPickerOptions}
                                                getOptionLabel={option => option.label}
                                                value={values.access_days}
                                                filterSelectedOptions
                                                onChange={value => {
                                                    const lastElement =
                                                        value[value.length - 1];
                                                    if (lastElement?.value === '') {
                                                        setFieldValue('access_days', [
                                                            lastElement
                                                        ]);
                                                    } else {
                                                        const updatedValue = value.filter(
                                                            val => val.value !== ''
                                                        );
                                                        setFieldValue(
                                                            'access_days',
                                                            updatedValue
                                                        );
                                                    }
                                                }}
                                                placeholder={
                                                    !values.access_days?.length
                                                        ? 'Select Access Days'
                                                        : ''
                                                }
                                                error={
                                                    touched.access_days &&
                                                    !!errors.access_days
                                                }
                                            />
                                            {returnTouched(touched, 'access_days') &&
                                                !!returnError(errors, 'access_days') && (
                                                    <FormHelperText>
                                                        {returnError(
                                                            errors,
                                                            'access_days'
                                                        )}
                                                    </FormHelperText>
                                                )}
                                        </FormControl>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <Stack spacing={0.5}>
                                        <InputLabel>Access Times</InputLabel>
                                        <FormControl error variant="standard">
                                            <Box>
                                                <TimePicker.RangePicker
                                                    popupStyle={{ zIndex: 1300 }}
                                                    defaultValue={values.time_range}
                                                    use12Hours
                                                    allowEmpty
                                                    id="time-range"
                                                    onChange={value =>
                                                        setFieldValue('time_range', value)
                                                    }
                                                    className={`${
                                                        returnTouched(
                                                            touched,
                                                            'time_range'
                                                        ) &&
                                                        !!returnError(
                                                            errors,
                                                            'time_range'
                                                        )
                                                            ? 'is-invalid'
                                                            : ''
                                                    }`}
                                                    status={
                                                        returnTouched(
                                                            touched,
                                                            'time_range'
                                                        ) &&
                                                        !!returnError(
                                                            errors,
                                                            'time_range'
                                                        )
                                                            ? 'error'
                                                            : ''
                                                    }
                                                    disabled={values.anytime_access}
                                                    onBlur={handleBlur('time_range')}
                                                    format={'HH:mm A'}
                                                />
                                                <FormControlLabel
                                                    value="end"
                                                    control={
                                                        <Checkbox
                                                            checked={
                                                                values.anytime_access
                                                            }
                                                            onChange={() =>
                                                                setFieldValue(
                                                                    'anytime_access',
                                                                    !values.anytime_access
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Anytime Access"
                                                    labelPlacement="end"
                                                    sx={{ ml: 1 }}
                                                />
                                            </Box>
                                            {returnTouched(touched, 'time_range') &&
                                                !!returnError(errors, 'time_range') && (
                                                    <FormHelperText>
                                                        {returnError(
                                                            errors,
                                                            'time_range'
                                                        )}
                                                    </FormHelperText>
                                                )}
                                        </FormControl>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions sx={{ p: 2.5 }}>
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Grid item>
                                    {isEdit && (
                                        <Tooltip
                                            title="Delete Visitor Invite"
                                            placement="top"
                                        >
                                            <IconButton
                                                size="large"
                                                color="error"
                                                onClick={() => {
                                                    onDelete(guestInvite);
                                                    onClose();
                                                }}
                                            >
                                                <DeleteFilled />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Grid>
                                <Grid item>
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        alignItems="center"
                                    >
                                        <Button color="error" onClick={onClose}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            onClick={handleSubmit}
                                        >
                                            {type === 'edit'
                                                ? 'Update Visitor Invite'
                                                : 'Invite Visitor'}
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogActions>
                    </>
                )}
            </Formik>
        </>
    );
};

export default InviteVisitorModal;
